import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, Truck, Lock } from "lucide-react";
import { toast } from "sonner";
import { formatINR, useCart } from "../context/CartContext";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import api, { apiError } from "../lib/api";
import { trackEvent, getStoredUTM } from "../lib/tracking";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, totals, clear, coupon } = useCart();
  const navigate = useNavigate();

  const [payment, setPayment] = useState("razorpay");
  const [loading, setLoading] = useState(false);
  const [codPartial, setCodPartial] = useState({ enabled: false, amount: 0 });

  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    address: "", apartment: "", city: "", state: "", pincode: "",
  });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/storefront/settings")
      .then(({ data }) => {
        setCodPartial({
          enabled: !!data?.store?.cod_partial_pay_enabled,
          amount: Number(data?.store?.cod_partial_pay_amount) || 0,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length > 0) {
      trackEvent("InitiateCheckout", {
        value: totals.total,
        currency: "INR",
        content_ids: items.map((i) => i.id),
        num_items: items.reduce((s, i) => s + i.quantity, 0),
      }, { em: form.email || undefined });
    }
    // eslint-disable-next-line
  }, []);

  // Fires once the shopper has typed a plausible email and moved on —
  // records the in-progress checkout so the abandoned-cart recovery sweep
  // (and the admin "Abandoned checkouts" list) has something to work
  // with if they never come back to place the order. Best-effort: a
  // failure here should never block checkout itself.
  const captureCart = () => {
    if (!/^\S+@\S+\.\S+$/.test(form.email) || items.length === 0) return;
    api.post("/cart/capture", {
      email: form.email,
      items: items.map((i) => ({
        product_id: i.id, name: i.name, price: i.price, size: i.size, quantity: i.quantity,
      })),
    }).catch(() => {});
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);

    const utm = getStoredUTM() || {};
    const fbpFbc = (() => {
      const get = (n) => {
        const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + n + "=([^;]*)"));
        return m ? decodeURIComponent(m[1]) : null;
      };
      return { fbp: get("_fbp"), fbc: get("_fbc") };
    })();

    try {
      const { data: order } = await api.post("/orders/create", {
        items: items.map((i) => ({
          product_id: i.id,
          name: i.name,
          price: i.price,
          image: i.image,
          color: i.color,
          size: i.size,
          quantity: i.quantity,
        })),
        address: {
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          address: form.address,
          apartment: form.apartment,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
        },
        payment_method: payment,
        coupon_code: coupon?.code || null,
        utm,
        fbp: fbpFbc.fbp,
        fbc: fbpFbc.fbc,
      });

      // Snapshot of everything the Thank You page needs to display —
      // built before clear() empties the cart/coupon state, since items/
      // totals/coupon are read from the closure here, not re-fetched.
      const orderSnapshot = {
        orderNumber: order.order_number,
        paymentMethod: payment,
        items: items.map((i) => ({
          name: i.name, size: i.size, quantity: i.quantity, price: i.price, image: i.image,
        })),
        totals: {
          subtotal: totals.subtotal,
          discount: totals.discount,
          couponCode: coupon?.code || null,
          shipping: totals.shipping,
          tax: totals.tax,
          total: totals.total,
        },
        contentIds: items.map((i) => i.id),
        email: form.email,
        phone: form.phone,
      };

      if (payment === "cod") {
        navigate("/thank-you", {
          state: { order: { ...orderSnapshot, value: totals.total, codPartialDue: null } },
        });
        clear();
        return;
      }

      // Razorpay flow
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay. Please check your connection.");

      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpay_order_id,
        name: "Flownté Clothing",
        description: `Order ${order.order_number}`,
        prefill: {
          name: `${form.first_name} ${form.last_name}`,
          email: form.email,
          contact: form.phone,
        },
        theme: { color: "#6D2C3B" },
        handler: async (response) => {
          try {
            await api.post("/orders/verify-payment", {
              order_id: order.order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            navigate("/thank-you", {
              state: {
                order: {
                  ...orderSnapshot,
                  // Full order value, not just what Razorpay charged just
                  // now — for cod_partial that's only the partial amount,
                  // and the Purchase event should reflect the whole
                  // transaction.
                  value: totals.total,
                  codPartialDue: payment === "cod_partial" ? totals.total - order.amount / 100 : null,
                },
              },
            });
            clear();
          } catch (verifyErr) {
            toast.error("Payment captured but verification failed. We'll reach out shortly.");
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.error("Payment cancelled");
          },
        },
      });
      rzp.on("payment.failed", () => {
        setLoading(false);
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      toast.error(apiError(err));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center">
        <h1 className="font-heading text-3xl text-ink-900">Your cart is empty</h1>
        <p className="mt-3 font-body text-ink-700">Add pieces to your cart before heading to checkout.</p>
        <Link to="/shop" className="btn-maroon mt-6 inline-block">Shop now</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pb-24">
      <section className="relative py-14 md:py-16 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 border-b border-blush-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-3xl text-blush-400">almost yours</span>
          <h1 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1">Checkout</h1>
          <p className="mt-2 font-body text-ink-500 text-sm inline-flex items-center gap-2">
            <Lock size={12} strokeWidth={1.5} /> Secure checkout · Razorpay
          </p>
        </div>
      </section>

      <form onSubmit={placeOrder} className="max-w-7xl mx-auto px-6 md:px-12 pt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-10">
          <div>
            <h2 className="font-heading text-2xl text-ink-900">Contact</h2>
            <input required type="email" placeholder="your@email.com" value={form.email} onChange={onChange("email")} onBlur={captureCart} className="field mt-4" data-testid="checkout-email" />
          </div>

          <div>
            <h2 className="font-heading text-2xl text-ink-900">Shipping address</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="First name" value={form.first_name} onChange={onChange("first_name")} className="field" data-testid="checkout-firstname" />
              <input required placeholder="Last name" value={form.last_name} onChange={onChange("last_name")} className="field" data-testid="checkout-lastname" />
              <input required placeholder="Phone" value={form.phone} onChange={onChange("phone")} className="field md:col-span-2" data-testid="checkout-phone" />
              <input required placeholder="Address" value={form.address} onChange={onChange("address")} className="field md:col-span-2" data-testid="checkout-address" />
              <input placeholder="Apartment, suite (optional)" value={form.apartment} onChange={onChange("apartment")} className="field md:col-span-2" />
              <input required placeholder="City" value={form.city} onChange={onChange("city")} className="field" data-testid="checkout-city" />
              <input required placeholder="State" value={form.state} onChange={onChange("state")} className="field" data-testid="checkout-state" />
              <input required placeholder="PIN code" value={form.pincode} onChange={onChange("pincode")} className="field md:col-span-2" data-testid="checkout-pincode" />
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl text-ink-900">Payment</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="mt-4 grid gap-3" data-testid="checkout-payment">
              {[
                { id: "razorpay", label: "Razorpay (Card · UPI · Netbanking · Wallets)", Icon: CreditCard, sub: "Secure prepaid checkout" },
                // Always exactly one COD-family option, never two: when
                // partial-pay is on (and the configured amount actually
                // applies to this order), the "Cash on Delivery" choice
                // itself collects the partial amount online — it never
                // sits alongside a separate plain-COD option.
                codPartial.enabled && codPartial.amount > 0 && codPartial.amount < totals.total
                  ? {
                      id: "cod_partial",
                      label: "Cash on Delivery",
                      Icon: Truck,
                      sub: `Pay ${formatINR(codPartial.amount)} now, ${formatINR(totals.total - codPartial.amount)} on delivery`,
                    }
                  : { id: "cod", label: "Cash on delivery", Icon: Truck, sub: "Pay when it arrives" },
              ].map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-4 rounded-2xl border p-4 cursor-pointer transition ${
                    payment === p.id ? "border-blush-600 bg-blush-50" : "border-blush-100 bg-white hover:border-blush-300"
                  }`}
                >
                  <RadioGroupItem value={p.id} id={p.id} className="border-blush-300 text-blush-600" />
                  <div className="h-10 w-10 rounded-full bg-white border border-blush-100 grid place-items-center text-blush-600">
                    <p.Icon size={16} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-body text-sm text-ink-900">{p.label}</p>
                    <p className="text-xs font-body text-ink-500">{p.sub}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-28 rounded-3xl bg-white border border-blush-100 p-6 md:p-8 shadow-sm">
            <h3 className="font-heading text-2xl text-ink-900">Your order</h3>
            <ul className="mt-5 space-y-4 max-h-[320px] overflow-y-auto pr-1">
              {items.map((i) => (
                <li key={i.key} className="flex gap-3">
                  <div className="relative h-16 w-14 rounded-xl overflow-hidden">
                    {i.image && <img src={i.image} alt="" className="h-full w-full object-cover" />}
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-blush-600 text-white text-[10px] grid place-items-center">
                      {i.quantity}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-ink-900 truncate">{i.name}</p>
                    <p className="font-body text-xs text-ink-500">Size {i.size}</p>
                  </div>
                  <p className="font-body text-sm text-ink-900">{formatINR(i.price * i.quantity)}</p>
                </li>
              ))}
            </ul>

            <dl className="mt-6 space-y-2 font-body text-ink-700 text-sm">
              <div className="flex items-center justify-between"><dt>Subtotal</dt><dd>{formatINR(totals.subtotal)}</dd></div>
              {totals.discount > 0 && (
                <div className="flex items-center justify-between text-blush-600"><dt>Discount ({coupon?.code})</dt><dd>- {formatINR(totals.discount)}</dd></div>
              )}
              <div className="flex items-center justify-between"><dt>Shipping</dt><dd>{totals.shipping === 0 ? "Free" : formatINR(totals.shipping)}</dd></div>
              {totals.tax > 0 && (
                <div className="flex items-center justify-between"><dt>Tax</dt><dd>{formatINR(totals.tax)}</dd></div>
              )}
              <div className="h-px bg-blush-100 my-2" />
              <div className="flex items-center justify-between text-lg">
                <dt className="font-heading text-ink-900">Total</dt>
                <dd className="font-heading text-ink-900">{formatINR(totals.total)}</dd>
              </div>
            </dl>

            <button
              type="submit"
              disabled={loading}
              data-testid="checkout-place-btn"
              className="btn-maroon mt-8 w-full disabled:opacity-60"
            >
              {loading ? "Processing…" : `Place order · ${formatINR(totals.total)}`}
            </button>
            <p className="mt-3 text-[11px] text-center font-body text-ink-500 uppercase tracking-[0.22em]">
              Secure · Encrypted · Trusted
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
